import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import FilterModal from "../new_taxonomy/filter-modal";
import { displayModal } from "../../../actions/shared-actions";
import productsActions from "actions/products-actions";
import getSelectedFilters from "modules/get-selected-filters";
import RentalOptionIntent, { RentalOptions, ActionsTaken } from "helpers/rental-option-intent-helper";
import ActionLogger from "action-logger";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import MyRTRFilterFieldset from "components/source/new_taxonomy/myrtr-filter-fieldset";
import { isAnonymous } from "components/source/hoc/with-user-data";
import { HIDDEN_FILTERS, flagsAndExperimentNames, NEXT_GRID_TYPES, FILTERS_TO_PRESERVE } from "rtr-constants";
import { useOnLoadFlagOrExperiment } from "components/source/hoc/with-flags-and-experiments";

export const GridFilterModal = ({ handleFilterChange, handleSortChange }) => {
  const workingFilters = useSelector(state => state.workingFilters) || {};
  const nextGridType = useSelector(state => state.nextGridType) || "";
  const filterOptions = useSelector(state => state.filterOptions) || {};
  const displayedModal = useSelector(state => state.displayedModal);
  const isMobileViewport = useSelector(state => state.browser?.isMobileViewport);
  const shortlists = useSelector(state => state.shortlists) || [];
  const userData = useSelector(state => state.userData);
  const [openFilters, setOpenFilters] = useState([]);
  const dispatch = useDispatch();
  const router = useRouter();
  const sort = useSelector(state => state.sort);
  const query = { ...router.query };
  const [showMyRtrFiltersInClearance] = useOnLoadFlagOrExperiment(
    flagsAndExperimentNames.SHOW_MY_RTR_FILTERS_CLEARANCE
  );
  const [showMyRtrFiltersInNonClearance] = useOnLoadFlagOrExperiment(
    flagsAndExperimentNames.SHOW_MY_RTR_FILTERS_DESIGNER_CATEGORY_CURATION
  );

  const workingSort = useSelector(state => state.workingSort);

  // Listen for sort, zip & canonical sizes change
  useEffect(() => {
    if (sort) {
      query["sortOptions"] = sort;
      handleSortChange(); // fires the API request
    }
  }, [sort]);

  const onCloseAttributesModal = () => {
    dispatch(displayModal(false));
  };

  const isFilterOpen = filterKey => {
    return openFilters.includes(filterKey);
  };

  const getSelectionCount = filterKey => {
    if (Object.keys(workingFilters).includes(filterKey)) {
      return Array.isArray(workingFilters[filterKey]) ? workingFilters[filterKey].length : 0;
    }
    return 0;
  };

  const isAllHeartsFilterSelected = () => {
    // NW [EXPLANATION] 10/25/19: the all hearts filter value is propagated as ["true"]
    return workingFilters?.hearts?.[0] === "true";
  };

  // copied from grid-store-interface with slight change
  const onDeselectProductCollectionFilter = productCollectionId => {
    // NW [EXPLANATION] 10/29/19: remove the shortlist filter that was deselected from selected shortlist filters
    const updatedShortlistFilters = workingFilters.productCollections.filter(id => id !== productCollectionId);

    const updatedHeartsFilterValue = isAllHeartsFilterSelected() ? [] : workingFilters.hearts;
    const updatedFilters = {
      ...workingFilters,
      ...(updatedHeartsFilterValue && { hearts: updatedHeartsFilterValue }), // NW [EXPLANATION] 10/25/19: conditionally set hearts workingFilter if it is defined
      productCollections: updatedShortlistFilters,
    };

    updateQueryAndCallProducts(updatedFilters);
  };

  const renderMyRTRFilters = () => {
    // MyRTRFilters really contains two distinct filters
    const selectionCount =
      getSelectionCount("hearts") + getSelectionCount("depot_id") + getSelectionCount("productCollections"); // productCollections = shortlists
    const fieldsetHeader = selectionCount ? `My RTR (${selectionCount})` : "My RTR";
    const maxVisibleFiltersWhenCollapsed = isMobileViewport ? 4 : 6;

    if (!isAnonymous(userData)) {
      return (
        <MyRTRFilterFieldset
          keyName="My RTR"
          filterOpen={isFilterOpen}
          onToggleOpenFilters={onToggleOpenFilters}
          onChange={onFilterChange}
          submitFilters={onSelectingAllHearts}
          onClear={onClearAll}
          filters={workingFilters || {}}
          idSuffix="-side"
          maxCollapsedFilters={maxVisibleFiltersWhenCollapsed}
          onDeselectProductCollectionFilter={onDeselectProductCollectionFilter}
          shortlists={shortlists}
          fieldsetHeader={fieldsetHeader}
          isMinimizable={true}
        />
      );
    }
  };

  const onToggleOpenFilters = filterKey => {
    if (openFilters.includes(filterKey)) {
      setOpenFilters(() => openFilters.filter(openKey => openKey !== filterKey));
    } else {
      setOpenFilters(() => [...openFilters, filterKey]);
    }
  };

  const updateIntentToReserve = selectedFilters => {
    if (selectedFilters?.date) {
      RentalOptionIntent.setLastIntent(RentalOptions.RESERVE, ActionsTaken.DATE_FILTER_GRID, "grid");
    }
  };

  const updateQueryAndCallProducts = selectedFilters => {
    dispatch(productsActions.changeFilters(selectedFilters));
    handleFilterChange(query, selectedFilters);
  };

  const onSelectingAllHearts = filterData => {
    if (!filterData) {
      return;
    }

    updateIntentToReserve(filterData);
    updateQueryAndCallProducts(filterData);
  };

  const onFilterChange = filterData => {
    const selectedFilters = getSelectedFilters(filterData, workingFilters);
    if (!selectedFilters) {
      return;
    } else if (filterData.filterGroupKey === "productCollections" && filterData.selected) {
      //if the user applies a shortlist filter, clear the all hearts filter if applied.
      selectedFilters.hearts = [];
    } else if (filterData.filterGroupKey === "hearts" && filterData.selected === true) {
      //if the user applies all hearts filter, clear any shortlist filters applied.
      selectedFilters.productCollections = [];
    }

    updateIntentToReserve(selectedFilters);
    updateQueryAndCallProducts(selectedFilters);
  };

  const onClearAll = () => {
    const filtersToPreserve = [...FILTERS_TO_PRESERVE];
    if (nextGridType in (HIDDEN_FILTERS || {})) filtersToPreserve.push(HIDDEN_FILTERS[nextGridType][0]);

    const filtersToClear = Object.keys(workingFilters).filter(filterKey => !filtersToPreserve.includes(filterKey));
    dispatch(productsActions.clearFiltersOnly(filtersToClear));
    handleFilterChange(query, {});
    dispatch(displayModal(false));
  };

  const onChangeSort = sort => {
    // ... TODO follow up with PC-269
    ActionLogger.logAction({
      object_type: "grid",
      action: "grid_sort",
      sort_type: sort,
      sort_type_from: sort,
    });
  };

  return (
    <FilterModal
      key="attribute-modal"
      isOpen={true}
      onRequestClose={onCloseAttributesModal}
      filters={workingFilters}
      workingFilters={workingFilters}
      isFilterOpen={isFilterOpen}
      filterOptions={filterOptions}
      renderRTRFilters={() => {
        if (showMyRtrFiltersInClearance && nextGridType === NEXT_GRID_TYPES["clearance"]) return renderMyRTRFilters();
        if (showMyRtrFiltersInNonClearance && nextGridType !== NEXT_GRID_TYPES["clearance"])
          return renderMyRTRFilters();
        return;
      }}
      clearAttributeFilters={onClearAll}
      getFieldsetHeader={() => null}
      onChangeSort={() => onChangeSort()}
      selectedSort={workingSort}
      isDeadEndTextSearch={false}
      showLimitedFilterSet={false}
      expandFilters={() => {}}
      isMobileViewport={isMobileViewport}
      displayedModal={displayedModal}
      onToggleOpenFilters={onToggleOpenFilters}
      onFilterChange={onFilterChange}
      submitFilters={() => {}}
    />
  );
};

GridFilterModal.propTypes = {
  handleFilterChange: PropTypes.func,
  handleSortChange: PropTypes.func,
};

export default GridFilterModal;
